import React, { useMemo, useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Search as SearchIcon, X } from 'lucide-react';
import Layout from '../components/Layout';
import ContentCard from '../components/ContentCard';
import { Chips, Grid, EmptyState } from '../components/PageBits';
import { useSearch } from '../lib/api';
import { useCatalog } from '../context/CatalogContext';

export default function SearchPage() {
  const [params, setParams] = useSearchParams();
  const [q, setQ] = useState(params.get('q') || '');
  const [filter, setFilter] = useState('All');
  useEffect(() => { setQ(params.get('q') || ''); }, [params]);
  const { res: rawRes, loading } = useSearch(q);
  const res = q.trim() ? rawRes : null;
  const { allContent } = useCatalog();
  const items = useMemo(() => {
    if (!res) return [];
    let list = [...res.movies, ...res.shows, ...res.podcasts];
    if (filter === 'Movies') list = res.movies; if (filter === 'Shows') list = res.shows; if (filter === 'Podcasts') list = res.podcasts;
    if (filter === 'Originals') list = list.filter((c) => c.isOriginal);
    return list;
  }, [res, filter]);
  const trending = allContent.slice(0, 12);

  return (
    <Layout>
      <div className="px-[22px] pt-[26px] pb-10" data-testid="search-page">
        <h1 className="page-title mb-4">Search Clax</h1>
        <form onSubmit={(e) => { e.preventDefault(); setParams(q ? { q } : {}); }} className="search-pill flex items-center gap-3 px-5 !h-[52px] max-w-[720px]">
          <SearchIcon size={20} className="text-[#C7C4CB]" />
          <input autoFocus value={q} onChange={(e) => { setQ(e.target.value); }} placeholder="Movies, shows, podcasts, episodes, creators, actors, genres..." className="bg-transparent outline-none text-[15px] w-full placeholder:text-[#8A8694]" data-testid="search-page-input" />
          {q && <button type="button" onClick={() => { setQ(''); setParams({}); }}><X size={18} className="text-[#C7C4CB]" /></button>}
        </form>
        <Chips options={['All', 'Movies', 'Shows', 'Podcasts', 'Originals']} value={filter} onChange={setFilter} className="mt-4 mb-6" testId="search-filters" />
        {q.trim() && !res ? <div className="text-[13px] text-[#C7C4CB]">Searching...</div> : res ? (
          <>
            {(res.people.length > 0 || res.genres.length > 0) && (
              <div className="flex flex-wrap gap-2 mb-5">
                {res.people.map((p) => <button key={p} onClick={() => { setQ(p); setParams({ q: p }); }} className="chip">{p}</button>)}
                {res.genres.map((g) => <button key={g} onClick={() => { setQ(g); setParams({ q: g }); }} className="chip">{g}</button>)}
              </div>
            )}
            <div className="text-[13px] text-[#C7C4CB] mb-3" data-testid="search-count">{items.length} result{items.length !== 1 ? 's' : ''} for "{q}"</div>
            {items.length ? <div className="-mx-[22px]"><Grid>{items.map((c) => <ContentCard key={c.id} item={c} className="!w-full" />)}</Grid></div> : <div className="-mx-[22px]"><EmptyState icon={SearchIcon} title={`No results for "${q}"`} text="Try a title, actor, host or genre." /></div>}
            {res.episodes.length > 0 && (
              <div className="mt-8"><h2 className="row-title mb-3">Episodes</h2><div className="surface divide-y divide-[#26212F]">{res.episodes.map((e) => <div key={e.ep.id} className="p-3 text-[13.5px] flex gap-3 items-center"><img src={(e.show || e.podcast).image} alt="" className="w-20 h-12 rounded object-cover" /><div><div className="font-medium">{e.ep.title}</div><div className="text-[12px] text-[#8A8694]">{(e.show || e.podcast).title}{e.season ? ` \u2022 S${e.season} E${e.ep.number}` : ''}</div></div></div>)}</div></div>
            )}
          </>
        ) : (
          <><h2 className="row-title mb-3">Popular searches</h2><div className="-mx-[22px]"><Grid>{trending.map((c) => <ContentCard key={c.id} item={c} className="!w-full" />)}</Grid></div></>
        )}
      </div>
    </Layout>
  );
}
